import React from 'react'
import { Box, Avatar, Typography, Chip } from '@mui/material'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import MicOffIcon from '@mui/icons-material/MicOff'
import MicIcon from '@mui/icons-material/Mic'
import PersonIcon from '@mui/icons-material/Person'
import { parseRtmUsername } from '../utils/rtmUsernameBuilder'
import { isValidHttpUrl } from 'src/mentor/helpers/utilityFunctions'
import { mediaURL } from 'src/consumer/services'

/**
 * Custom Video Placeholder component for when camera is turned off
 * Shows a beautiful avatar with gradient background
 */
const CustomVideoPlaceholder = ({ user, isShown, showButtons, showSwap, uidToUsernameMap, localRtmUsername }) => {
  // Check if user has audio (mic is on)
  const hasAudio = user?.hasAudio === 1 || user?.hasAudio === true
  
  // State to store username extracted from DOM (for remote users when RTM data isn't in map)
  const [domExtractedName, setDomExtractedName] = React.useState(null)
  
  // Get the latest map data - always check window object for latest data (updated by parent)
  // This ensures we get the latest data even if the prop is stale
  const getLatestMap = () => {
    // Always check window object first (it's updated immediately by parent)
    if (typeof window !== 'undefined' && window.__latestUidToUsernameMap) {
      const windowMap = window.__latestUidToUsernameMap
      // If window map has data, use it (it's always the latest)
      if (windowMap && Object.keys(windowMap).length > 0) {
        return windowMap
      }
    }
    // Fallback to prop
    return uidToUsernameMap || {}
  }
  
  // Extract username from DOM - simple approach: find video card and get all text, pick the best match
  const extractUsernameFromDOM = React.useCallback((uid) => {
    try {
      // Find video card for this uid - try all possible ways
      let videoCard = null
      
      // Method 1: Direct query
      videoCard = document.querySelector(`div[data-uid="${uid}"]`) || 
                  document.querySelector(`[data-uid="${uid}"]`)
      
      // Method 2: Search all data-uid elements
      if (!videoCard) {
        const allUidElements = document.querySelectorAll('[data-uid]')
        for (const el of allUidElements) {
          const elUid = el.getAttribute('data-uid')
          if (String(elUid) === String(uid)) {
            videoCard = el
            break
          }
        }
      }
      
      // Method 3: Search in video containers
      if (!videoCard) {
        const containers = document.querySelectorAll('[class*="video"], [class*="Video"], [class*="container"]')
        for (const container of containers) {
          const found = container.querySelector(`[data-uid="${uid}"]`)
          if (found) {
            videoCard = found
            break
          }
        }
      }
      
      if (!videoCard) return null
      
      // Get ALL text content from the video card and its children
      const allText = videoCard.innerText || videoCard.textContent || ''
      const textLines = allText.split('\n').map(t => t.trim()).filter(t => t.length > 0)
      
      // Find the best candidate that looks like a name
      for (const text of textLines) {
        // Skip if it's clearly not a name
        if (text.includes('{') || 
            text.includes('"name"') || 
            text.match(/^User\s+\d+$/) ||
            text.match(/^\d+$/) ||
            text.length < 2 ||
            text.length > 100 ||
            text.match(/^[A-Z]$/) || // Single letter
            text.toLowerCase() === 'me' ||
            text.toLowerCase() === 'you' ||
            text.toLowerCase() === 'loading' ||
            text.toLowerCase().includes('camera') ||
            text.toLowerCase().includes('microphone') ||
            text.toLowerCase().includes('mute') ||
            text.toLowerCase().includes('unmute')) {
          continue
        }
        
        // This looks like a name - return it
        return text
      }
      
      // Fallback: try to find elements with specific classes/attributes
      const candidates = videoCard.querySelectorAll('span, p, div, [class*="username"], [class*="name"]')
      for (const el of candidates) {
        const text = (el.textContent || el.innerText || '').trim()
        if (text && 
            text.length > 1 && 
            text.length < 100 &&
            !text.includes('{') && 
            !text.includes('"name"') && 
            !text.match(/^User\s+\d+$/) &&
            !text.match(/^\d+$/) &&
            !text.match(/^[A-Z]$/) &&
            text.toLowerCase() !== 'me' &&
            text.toLowerCase() !== 'you' &&
            text.toLowerCase() !== 'loading') {
          return text
        }
      }
    } catch (e) {
      // DOM extraction failed
    }
    return null
  }, [])
  
  // Use MutationObserver to watch for name pill updates (since name pill works, we can observe it)
  React.useEffect(() => {
    // Only for remote users and if we don't have RTM data
    if (user?.uid !== undefined && user?.uid !== 0) {
      // Check if we already have RTM data (always get latest map)
      const mapToCheck = getLatestMap()
      const hasRtmData = mapToCheck?.[user.uid] || 
                        mapToCheck?.[String(user.uid)] || 
                        mapToCheck?.[Number(user.uid)]
      
      if (!hasRtmData) {
        // Find the video card for this uid
        const findVideoCard = () => {
          let card = document.querySelector(`div[data-uid="${user.uid}"]`)
          if (!card) card = document.querySelector(`[data-uid="${user.uid}"]`)
          if (!card) {
            const allUidElements = document.querySelectorAll('[data-uid]')
            for (const el of allUidElements) {
              if (String(el.getAttribute('data-uid')) === String(user.uid)) {
                card = el
                break
              }
            }
          }
          return card
        }
        
        // Try to find name pill and observe it
        const setupObserver = () => {
          const videoCard = findVideoCard()
          if (!videoCard) return null
          
          // Create observer to watch for text changes in the video card
          const observer = new MutationObserver((mutations) => {
            // After any mutation, try to extract the name
            // Use a small delay to ensure the DOM has settled
            setTimeout(() => {
              const extracted = extractUsernameFromDOM(user.uid)
              if (extracted && extracted !== domExtractedName) {
                setDomExtractedName(extracted)
              }
              
              // Also try to find name pill elements directly
              const allNameElements = document.querySelectorAll('[class*="username"], span, p, div[style*="background"]')
              for (const el of allNameElements) {
                // Check if this element is within the video card for this uid
                const closestUid = el.closest('[data-uid]')
                if (closestUid && String(closestUid.getAttribute('data-uid')) === String(user.uid)) {
                  const text = (el.textContent || el.innerText || '').trim()
                  if (text && 
                      text.length > 1 && 
                      text.length < 100 &&
                      !text.includes('{') && 
                      !text.includes('"name"') && 
                      !text.match(/^User\s+\d+$/) &&
                      !text.match(/^\d+$/) &&
                      text !== domExtractedName) {
                    setDomExtractedName(text)
                    break
                  }
                }
              }
            }, 100)
          })
          
          // Observe the video card and all its descendants
          observer.observe(videoCard, {
            childList: true,
            subtree: true,
            characterData: true,
          })
          
          return observer
        }
        
        // Set up observer after a delay to allow DOM to be ready
        const observerTimeout = setTimeout(() => {
          const observer = setupObserver()
          if (observer) {
            // Also do an immediate check
            const extracted = extractUsernameFromDOM(user.uid)
            if (extracted) {
              setDomExtractedName(extracted)
            }
          }
        }, 500)
        
        // Also do periodic checks as fallback - check more frequently
        const checkInterval = setInterval(() => {
          const extracted = extractUsernameFromDOM(user.uid)
          if (extracted && extracted !== domExtractedName) {
            setDomExtractedName(extracted)
          }
        }, 500) // Check every 500ms
        
        return () => {
          clearTimeout(observerTimeout)
          clearInterval(checkInterval)
        }
      } else {
        // We have RTM data, clear DOM extracted name
        if (domExtractedName) {
          setDomExtractedName(null)
        }
      }
    }
  }, [user?.uid, uidToUsernameMap, extractUsernameFromDOM, domExtractedName])
  
  // Get parsed user info from RTM username (handles missing fields gracefully)
  const getUserInfo = () => {
    try {
      // Always get the latest map data (from window object, updated by parent)
      // Force a fresh read from window object every time
      const mapToUse = (typeof window !== 'undefined' && window.__latestUidToUsernameMap) 
        ? window.__latestUidToUsernameMap 
        : (uidToUsernameMap || {})
      
      // Safely get username from mapping - use robust lookup that handles string/number keys
      let rawUsername = null
      if (mapToUse && user?.uid !== undefined) {
        const uid = user.uid
        const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : Number(uid)
        const uidStr = String(uid)
        
        // Debug: log lookup attempt for remote users
        if (user.uid !== 0) {
          console.log('[CustomVideoPlaceholder] Looking up username for remote user:', {
            uid,
            uidType: typeof uid,
            uidNum,
            uidStr,
            mapSize: Object.keys(mapToUse).length,
            mapKeys: Object.keys(mapToUse),
          })
        }
        
        // First, try direct lookups with all possible key formats
        rawUsername = mapToUse[uid] || 
                      mapToUse[uidNum] || 
                      mapToUse[uidStr] ||
                      mapToUse[String(uidNum)] ||
                      mapToUse[Number(uidStr)]
        
        // If still not found, iterate through ALL map keys and compare as strings
        // This is the most reliable way to find a match regardless of key type
        if (!rawUsername && Object.keys(mapToUse).length > 0) {
          for (const [key, value] of Object.entries(mapToUse)) {
            // Convert both to strings for comparison - this handles all cases
            const keyStr = String(key)
            const uidStrForCompare = String(uid)
            
            // Also try numeric comparison if both are valid numbers
            const keyNum = Number(key)
            const uidNumForCompare = Number(uid)
            
            // Match if strings match OR numbers match (and both are valid numbers)
            if (keyStr === uidStrForCompare || 
                (!isNaN(keyNum) && !isNaN(uidNumForCompare) && keyNum === uidNumForCompare)) {
              rawUsername = value
              if (user.uid !== 0) {
                console.log('[CustomVideoPlaceholder] Found username via iteration:', {
                  uid,
                  matchedKey: key,
                  keyType: typeof key,
                  usernamePreview: typeof value === 'string' ? value.substring(0, 50) + '...' : value,
                })
              }
              break
            }
          }
        }
        
        // Debug: log result for remote users
        if (user.uid !== 0) {
          console.log('[CustomVideoPlaceholder] Lookup result:', {
            uid,
            found: !!rawUsername,
            usernamePreview: rawUsername ? (typeof rawUsername === 'string' ? rawUsername.substring(0, 50) + '...' : rawUsername) : null,
          })
        }
      }
      
      // For local user (uid === 0), if not found in map, use localRtmUsername as fallback
      // This handles the case where the map hasn't been populated yet
      if (!rawUsername && user?.uid === 0 && localRtmUsername) {
        rawUsername = localRtmUsername
      }
      
      // For remote users, if not found in map, try using DOM-extracted name
      if (!rawUsername && user?.uid !== undefined && user?.uid !== 0 && domExtractedName) {
        // We found the name in DOM, construct a minimal RTM username format
        // Since we don't have profilePicId/role from DOM, we'll use just the name
        // This is better than showing "U" or "Loading..."
        rawUsername = JSON.stringify({ name: domExtractedName, role: null, profilePicId: null })
      }
      
      // Check if RTM data has been received
      const hasRtmData = !!rawUsername
      
      // Parse with safe defaults
      const parsed = parseRtmUsername(rawUsername)
      
      // CRITICAL FIX: For remote users, if the parsed data has role='mentor' or contains
      // mentor-specific data, it might be incorrectly mapped. Remote users should NOT
      // get mentor profile pictures. This prevents the bug where remote users show
      // local user's profile picture due to RTM/RTC UID mapping issues.
      let profilePicId = parsed.profilePicId || null
      let isMismappedData = false
      if (user?.uid !== 0 && parsed.role === 'mentor') {
        // Remote user has mentor data - this is likely a mapping error
        // Clear the profilePicId to prevent showing wrong picture
        // But keep using the name and show initials (better than showing nothing)
        profilePicId = null
        isMismappedData = true
        console.warn('[CustomVideoPlaceholder] Remote user has mentor role data - clearing profilePicId but keeping name for initials', {
          uid: user?.uid,
          parsedData: parsed,
        })
      }
      
      // Determine display name:
      // - If local user (uid === 0), use parsed name from RTM data
      // - If RTM data not received, fallback to "You" (not "Me")
      // - If RTM data received, use parsed name (but not "Unknown")
      // - For remote users, fallback to "Guest" if truly unknown
      // - For mismapped data, still use the name (it's better than nothing and shows initials)
      let displayName
      if (user?.uid === 0) {
        // For local user, get name from RTM data if available
        if (hasRtmData && parsed.name && parsed.name !== 'Unknown') {
          displayName = parsed.name
        } else if (!hasRtmData) {
          displayName = 'You' // Fallback when RTM data not received
        } else {
          displayName = 'You' // Fallback if name not available
        }
      } else if (hasRtmData && parsed.name && parsed.name !== 'Unknown') {
        // For remote users, use parsed name even if it's mismapped
        // This allows initials to be shown (better UX than blank)
        displayName = parsed.name
      } else if (!hasRtmData) {
        displayName = 'Loading...' // RTM data pending
      } else {
        displayName = 'Guest' // Fallback
      }
      
      // Always return safe object with defaults
      return {
        role: parsed.role || null,
        name: displayName,
        profilePicId: profilePicId, // Use the validated profilePicId (null if mismapped)
        hasRtmData,
        isMismappedData, // Track if this is mismapped data for debugging
      }
    } catch (error) {
      // If anything fails, return safe defaults
      console.warn('Error parsing RTM username:', error)
      return {
        role: null,
        name: user?.uid === 0 ? 'You' : 'Loading...',
        profilePicId: null,
        hasRtmData: false,
      }
    }
  }
  
  // Use useMemo to recalculate userInfo - but also add a periodic check to get latest data
  // This ensures the component gets updated data even if props don't change
  const [updateTrigger, setUpdateTrigger] = React.useState(0)
  
  // Use ref to persist lastFoundUsername across interval calls
  const lastFoundUsernameRef = React.useRef(null)
  
  // Periodically check for map updates (every 500ms) to catch when window object is updated
  // This ensures we detect when RTM data arrives for remote users
  React.useEffect(() => {
    if (user?.uid !== undefined && user?.uid !== 0) {
      // Reset ref when uid changes
      lastFoundUsernameRef.current = null
      
      const interval = setInterval(() => {
        // Check if window map has new data - always read fresh from window
        const latestMap = (typeof window !== 'undefined' && window.__latestUidToUsernameMap) 
          ? window.__latestUidToUsernameMap 
          : (uidToUsernameMap || {})
        
        const uid = user.uid
        const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : Number(uid)
        const uidStr = String(uid)
        
        // Try all possible key formats to find username
        let rawUsername = latestMap?.[uid] || 
                          latestMap?.[uidNum] || 
                          latestMap?.[uidStr] ||
                          latestMap?.[String(uidNum)] ||
                          latestMap?.[Number(uidStr)]
        
        // If still not found, iterate through map keys with very permissive matching
        if (!rawUsername && Object.keys(latestMap).length > 0) {
          for (const [key, value] of Object.entries(latestMap)) {
            const keyNum = Number(key)
            const keyStr = String(key)
            if (!isNaN(keyNum)) {
              // Very permissive matching - try all combinations
              if (keyNum === uidNum || 
                  keyNum === uid || 
                  keyStr === uidStr || 
                  keyStr === String(uid) ||
                  key === uidStr || 
                  key === String(uidNum) ||
                  String(keyNum) === String(uid) ||
                  String(keyNum) === uidStr) {
                rawUsername = value
                break
              }
            } else if (key === uidStr || key === String(uid)) {
              rawUsername = value
              break
            }
          }
        }
        
        // If we found data and it's different from what we had, trigger update
        if (rawUsername && rawUsername !== lastFoundUsernameRef.current) {
          lastFoundUsernameRef.current = rawUsername
          // Force immediate update
          setUpdateTrigger(prev => prev + 1)
        } else if (!rawUsername && lastFoundUsernameRef.current) {
          // If we had data before but don't now, also update (data might have been cleared)
          lastFoundUsernameRef.current = null
          setUpdateTrigger(prev => prev + 1)
        }
      }, 500) // Check every 500ms for faster updates
      return () => clearInterval(interval)
    }
  }, [user?.uid, uidToUsernameMap])
  
  const userInfo = React.useMemo(() => getUserInfo(), [user?.uid, uidToUsernameMap, localRtmUsername, domExtractedName, updateTrigger])
  
  // Get profile picture URL from profilePicId
  const getProfilePictureUrl = () => {
    if (!userInfo.profilePicId) return null
    
    // Check if it's already a full URL
    if (isValidHttpUrl(userInfo.profilePicId)) {
      return userInfo.profilePicId
    }
    
    // Otherwise, construct URL from mediaURL
    return `${mediaURL}${userInfo.profilePicId}`
  }
  
  const profilePictureUrl = getProfilePictureUrl()
  
  // Extract first letter of name for avatar (with safe fallback)
  // Returns string for initials OR null to show person icon
  const getInitials = () => {
    const name = userInfo.name
    if (name && name.length > 0 && name !== 'Loading...' && name !== 'You') {
      // Get first letter, or first letter of each word if multiple words
      const words = name.trim().split(/\s+/)
      if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      }
      return name.charAt(0).toUpperCase()
    }
    // Fallback for local user if name not available
    if (user?.uid === 0) return 'Y' // "You" -> "Y"
    // For remote users without name, return null to show person icon
    return null
  }
  
  // Get display name from parsed RTM username (always returns a string)
  const getDisplayName = () => {
    // userInfo.name already has the correct logic (You, Loading..., or actual name)
    return userInfo.name || (user?.uid === 0 ? 'You' : 'Loading...')
  }

  // Generate a consistent modern color based on uid
  const getAvatarColor = () => {
    if (user?.uid === 0) {
      // Local user - use vibrant purple theme colors
      return {
        background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 50%, #7c3aed 100%)',
        shadow: 'rgba(139, 92, 246, 0.6)',
        glow: 'rgba(167, 139, 250, 0.4)',
      }
    }
    
    // Remote users - modern vibrant gradients
    const colors = [
      { 
        bg: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)', 
        shadow: 'rgba(139, 92, 246, 0.6)',
        glow: 'rgba(139, 92, 246, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 50%, #e11d48 100%)', 
        shadow: 'rgba(236, 72, 153, 0.6)',
        glow: 'rgba(236, 72, 153, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)', 
        shadow: 'rgba(6, 182, 212, 0.6)',
        glow: 'rgba(6, 182, 212, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)', 
        shadow: 'rgba(16, 185, 129, 0.6)',
        glow: 'rgba(16, 185, 129, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)', 
        shadow: 'rgba(245, 158, 11, 0.6)',
        glow: 'rgba(245, 158, 11, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #4338ca 100%)', 
        shadow: 'rgba(99, 102, 241, 0.6)',
        glow: 'rgba(99, 102, 241, 0.4)',
      },
    ]
    
    const index = (user?.uid || 0) % colors.length
    return colors[index]
  }

  const colorScheme = getAvatarColor()

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Glass morphism background
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        // Subtle gradient overlay
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(99, 102, 241, 0.08) 50%, rgba(236, 72, 153, 0.12) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
        // Match video card border radius to stay within bounds
        border: 'none',
        borderRadius: 'inherit', // Inherit from parent video card
        overflow: 'hidden',
        // Ensure content is above overlay
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {/* Status indicators only - no name badge - positioned at bottom right */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {/* Camera status - icon only */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: '#FEE2E2',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 12, color: '#DC2626' }} />
        </Box>
        
        {/* Mic status - icon only */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: hasAudio ? '#D1FAE5' : '#FEE2E2',
            border: hasAudio ? '1px solid #A7F3D0' : '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasAudio ? (
            <MicIcon sx={{ fontSize: 12, color: '#059669' }} />
          ) : (
            <MicOffIcon sx={{ fontSize: 12, color: '#DC2626' }} />
          )}
        </Box>
      </Box>

      {/* Beautiful avatar with profile picture or gradient */}
      <Box
        sx={{
          position: 'relative',
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Avatar
          src={profilePictureUrl || undefined}
          sx={{
            width: 88,
            height: 88,
            background: profilePictureUrl ? 'transparent' : colorScheme.background,
            boxShadow: profilePictureUrl 
              ? `0 8px 32px ${colorScheme.shadow}, 0 0 0 4px rgba(255, 255, 255, 0.2), 0 0 0 1px ${colorScheme.glow}` 
              : `0 8px 32px ${colorScheme.shadow}, 0 4px 16px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.3)`,
            fontSize: '36px',
            fontWeight: 700,
            border: '0',
            backdropFilter: 'blur(12px)',
            animation: 'gentle-pulse 4s ease-in-out infinite, subtle-rotate 20s linear infinite',
            objectFit: 'cover',
            color: '#ffffff',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.5)',
            letterSpacing: '1px',
            position: 'relative',
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              right: '-4px',
              bottom: '-4px',
              background: colorScheme.background,
              borderRadius: '50%',
              opacity: 0.3,
              filter: 'blur(12px)',
              zIndex: -1,
            },
            '@keyframes gentle-pulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: `0 8px 32px ${colorScheme.shadow}, 0 4px 16px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.3)`,
              },
              '50%': {
                transform: 'scale(1.05)',
                boxShadow: `0 12px 40px ${colorScheme.shadow}, 0 6px 24px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4)`,
              },
            },
            '@keyframes subtle-rotate': {
              '0%': {
                filter: 'hue-rotate(0deg)',
              },
              '100%': {
                filter: 'hue-rotate(15deg)',
              },
            },
          }}
          imgProps={{
            style: {
              borderRadius: '50%',
              objectFit: 'cover',
            },
            onError: (e) => {
              // If image fails to load, hide src to show initials or person icon
              e.target.style.display = 'none'
            },
          }}
        >
          {!profilePictureUrl && (() => {
            const initials = getInitials()
            // If getInitials returns null, show person icon instead
            if (initials === null) {
              return <PersonIcon sx={{ fontSize: 52, color: 'rgba(255, 255, 255, 0.95)', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />
            }
            // Otherwise show initials
            return initials
          })()}
        </Avatar>
        
        {/* Name and (You) text below avatar - only for local user */}
        {user?.uid === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.87)',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {userInfo.name !== 'Loading...' ? userInfo.name : 'You'}
            </Typography>
            {/* Only show (You) if name is not "You" (i.e., if we have actual name from RTM) */}
            {userInfo.name !== 'You' && userInfo.name !== 'Loading...' && (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                (You)
              </Typography>
            )}
          </Box>
        )}
      </Box>

    </Box>
  )
}

export default CustomVideoPlaceholder

